import React, { useEffect, useRef, useState } from "react";
import { normalizeOptions } from "../../utils/miscelleneous";
import useRecorder from "../../utils/useRecorder";
import AudioPlayer from "../../utils/audioPlayer";
import { useDispatch } from "react-redux";
import { uploadAudio } from "../../Redux/Action/apiAction";

const ReadingSection = ({ section, flattenedQuestions = [], submitAnswer, onRequestNextSection, assignmentId }) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [localAnswers, setLocalAnswers] = useState({});
  const [recordingState, setRecordingState] = useState({});
  const [recTimer, setRecTimer] = useState(60);
  const [isPlaying, setIsPlaying] = useState(false);

  const { startRecording, stopRecording, reset, isRecording, audioBlob, audioURL, durationSeconds } = useRecorder();

  const isCompleted = (q) => {
    if (!q?.previous_answer?.answer) return false;
    const data = q.previous_answer.answer.answer_data;
    if (data?.status === "completed") return true;
    if (typeof data === "string" && data.trim() !== "") return true;
    if (typeof data === "object" && Object.keys(data).length > 0) return true;
    return false;
  };

  useEffect(() => {
    let nextIndex = index;
    while (
      nextIndex < flattenedQuestions.length &&
      isCompleted(flattenedQuestions[nextIndex])
    ) {
      nextIndex++;
    }
    if (nextIndex !== index) {
      if (nextIndex >= flattenedQuestions.length) {
        onRequestNextSection && onRequestNextSection();
      } else {
        setIndex(nextIndex);
      }
    }
  }, [section, index, flattenedQuestions]);

  useEffect(() => {
    setIndex(0);
    setLocalAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [section]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopRecording();
    };
  }, [section]);

  if (!section) return <div className="language-right-section">No section data</div>;

  const current = flattenedQuestions[index];

  if (!current) return <div className="language-right-section">No questions</div>;

  const isParent = !!current._isParent;
  const parentPassage = isParent ? current.content?.passage : (flattenedQuestions.slice(0, index).reverse().find(p => p._isParent && p.groupId === current.groupId) || null);

  const handleMCQSelect = (assignment_question_id, opt) => {
    setLocalAnswers((s) => ({
      ...s,
      [assignment_question_id]: {
        answer_type: "multiple_choice",
        selected_option_id: opt.id,
        selected_option: `${opt.id}) ${opt.text}`
      }
    }));
  };

  const handleTextChange = (assignment_question_id, text) => {
    setLocalAnswers((s) => ({ ...s, [assignment_question_id]: { answer_type: "text_input", text_answer: text } }));
  };

  const startTimer = () => {
    setRecTimer(60);
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setRecTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          stopRecording();
          setRecordingState((s) => ({
            ...s,
            [current.id]: { isRecording: false, recorded: true }
          }));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
  };

  const handleUploadRecording = async () => {
    if (!audioBlob) {
      setRecordingState((s) => ({
        ...s,
        [current.id]: { error: "No recording found" },
      }));
      return;
    }

    setRecordingState((s) => ({
      ...s,
      [current.id]: { uploading: true },
    }));

    const formData = new FormData();
    formData.append("audio_file", audioBlob, `reading_${current.id}.webm`);
    formData.append("assessment_id", assignmentId);
    formData.append("question_id", current.id);
    formData.append("question_type", current.content?.question_type);
    formData.append("assignment_question_id", current.assignment_question_id);

    const res = await dispatch(uploadAudio(formData));

    if (!res?.success || !res.payload?.audio_url) {
      setRecordingState((s) => ({
        ...s,
        [current.id]: { uploading: false, error: "Upload failed" },
      }));
      return;
    }

    const answer = {
      answer_data: {
        type: "audio_recording",
        audio_url: res.payload.audio_url,
        duration_seconds: res.payload.duration_seconds ?? durationSeconds,
        status: "completed",
        timestamp: new Date().toISOString(),
      }
    };

    submitAnswer({
      assignment_question_id: current.assignment_question_id,
      question_id: current.id,
      answer,
    });

    if (index < flattenedQuestions.length - 1) {
      setIndex(index + 1);
      reset();
    } else {
      onRequestNextSection && onRequestNextSection();
    }
  };

  const handleSubmitMCQ = () => {
    const assignment_question_id = current.assignment_question_id;
    const local = localAnswers[assignment_question_id];
    if (!local) return;

    submitAnswer({
      assignment_question_id,
      question_id: current.id,
      answer: { answer_data: local.selected_option || local.text_answer },
    });

    if (index < flattenedQuestions.length - 1) {
      setIndex(index + 1);
    } else {
      onRequestNextSection && onRequestNextSection();
    }
  };

  const isAnswered = !!localAnswers[current.assignment_question_id];

  return (
    <>
      <div className="language-left-section reading" style={{ gap: "13px" }}>

        {/* Parent Passage */}
        {current._isParent && current.content?.question_type === "reading_comprehension_set" && (
          <div className="passage-box">
            <p style={{ maxWidth: '450px', width: '100%', overflow: 'auto' }}>{current.content?.paragraph}</p>
          </div>
        )}

        {/* Child Questions – show same passage */}
        {!current._isParent &&
          parentPassage &&
          parentPassage.content?.paragraph && (
            <div className="passage-box">
              <p style={{ maxWidth: '450px', width: '100%', overflow: 'auto' }}>{parentPassage.content.paragraph}</p>
            </div>
          )}

        {/* Reading Fluency - text to read aloud */}
        {current.content?.question_type === "reading_fluency_individual" && (
          <div className="passage-box">
            <p style={{ maxWidth: '450px', width: '100%', overflow: 'auto' }}>{current.text}</p>
          </div>
        )}
      </div>
      <div className="language-right-section">
        <div className="mcq">
          {/* PARENT ONLY: JUST CONTINUE BUTTON */}
          {current._isParent &&
            current.content?.question_type === "reading_comprehension_set" && (
              <>
                <p>{current.content.instructions}</p>
                <button
                  className="submit-btn"
                  onClick={() => {
                    submitAnswer({
                      assignment_question_id: current.assignment_question_id,
                      question_id: current.id,
                      answer: { answer_data: "viewed" }
                    });
                    setIndex(index + 1);
                  }}
                >
                  Continue
                </button>
              </>
            )}

          {/* CHILD MCQ QUESTIONS */}
          {!current._isParent && current.content?.options && (
            <>
              <h5>{current.text}</h5>

              <div className="option-grid">
                {normalizeOptions(current.content.options).map((opt, i) => {
                  const idKey = current.assignment_question_id || current.id;

                  const selected =
                    localAnswers[idKey]?.selected_option_id === opt.id;

                  return (
                    <div
                      key={i}
                      className={`opt-box ${selected ? "selected" : ""}`}
                      onClick={() => handleMCQSelect(idKey, opt)}
                    >
                      <span className="opt-label">{opt.id}</span>
                      <span className="opt-text">{opt.text}</span>
                    </div>
                  );
                })}
              </div>

              <button
                className={`submit-btn ${!isAnswered ? "disabled" : ""}`}
                disabled={!isAnswered}
                onClick={handleSubmitMCQ}
              >
                Submit
              </button>
            </>
          )}

          {/* TEXT INPUT QUESTIONS */}
          {!current._isParent &&
            !current.content?.options &&
            current.content?.question_type === "text_input" && (
              <>
                <h5>{current.text}</h5>
                <textarea
                  placeholder="Type your answer..."
                  onChange={(e) =>
                    handleTextChange(
                      current.assignment_question_id || current.id,
                      e.target.value
                    )
                  }
                />
                <button
                  className={`submit-btn ${!isAnswered ? "disabled" : ""}`}
                  disabled={!isAnswered}
                  onClick={handleSubmitMCQ}
                >
                  Submit
                </button>
              </>
            )}

          {/* READING FLUENCY RECORDING */}
          {current.content?.question_type === "reading_fluency_individual" && (
            <>
              <p className="instructions">{current.content.instructions}</p>

              <div className="speaking-right" style={{ textAlign: "center" }}>
                <p style={{ marginBottom: "0" }}>
                  {!recordingState[current.id]?.recorded
                    ? "Record your response by clicking the mic below"
                    : "Listen to your recording or submit"}
                </p>


                {/* BEFORE RECORDING */}
                {!isRecording && !recordingState[current.id]?.recorded && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }}
                    onClick={() => {
                      reset();
                      setRecordingState((s) => ({
                        ...s,
                        [current.id]: { isRecording: true, recorded: false }
                      }));
                      startRecording();
                      startTimer();
                    }}
                  >
                    <img title="Start Recording" src="/images/mic-icon.svg" alt="" width={56} height={56} />
                  </div>
                )}

                {/* DURING RECORDING */}
                {isRecording && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }}
                    onClick={() => {
                      stopTimer();
                      stopRecording();
                      setRecordingState((s) => ({
                        ...s,
                        [current.id]: { isRecording: false, recorded: true }
                      }));
                    }}
                  >
                    <img title="Stop Recording" src="/images/reddot-img.svg" alt="" width={71} height={71} />
                  </div>
                )}

                {/* AFTER RECORDING — PLAY/PAUSE */}
                {!isRecording && recordingState[current.id]?.recorded && audioURL && (
                  <div className="wight-mic-bg" style={{ cursor: "pointer", margin: "15px auto" }} onClick={togglePlay}>
                    <img title="Play-Pause" src={isPlaying ? "/images/pause-icon.svg" : "/images/play-icon.svg"} alt="" width={56} height={56} />
                  </div>
                )}

                {/* Timer During Recording */}
                {isRecording && (<p style={{ marginBottom: "12px" }}>Recording… {recTimer}s left</p>)}

                {/* Waveform */}
                {!isRecording && recordingState[current.id]?.recorded && audioURL && (
                  <AudioPlayer audioUrl={audioURL} ref={audioRef} onPlayStateChange={(state) => setIsPlaying(state)} />
                )}

                {/* Upload & Submit */}
                {!isRecording && recordingState[current.id]?.recorded && (
                  <button
                    className="primary"
                    onClick={handleUploadRecording}
                    disabled={recordingState[current.id]?.uploading}
                    style={{ marginTop: "12px" }}
                  >
                    {recordingState[current.id]?.uploading ? "Uploading…" : "Submit"}
                  </button>
                )}

                {/* Rerecord */}
                {!isRecording && recordingState[current.id]?.recorded && (
                  <p
                    onClick={() => {
                      reset();
                      setRecordingState((s) => ({
                        ...s,
                        [current.id]: { isRecording: false, recorded: false }
                      }));
                    }}
                    style={{ cursor: "pointer", marginTop: "6px", width: 'unset' }}
                  >
                    <img src="/images/refresh-outline.svg" alt="" width={16} height={16} /> Record Again
                  </p>
                )}

                {recordingState[current.id]?.error && (
                  <p className="error">{recordingState[current.id]?.error}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ReadingSection;